import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { userAtom } from '@/atoms/user.atom';
import { useDialog } from '@/providers/DialogProvider';
import { Key, Shield } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangePasswordDialog } from './dialogs/ChangePasswordDialog';
import { Setup2FADialog } from './dialogs/Setup2FADialog';

interface iLabelProps {
  title: string;
  description: string;
}
const Label: FunctionComponent<iLabelProps> = ({ title, description }) => {
  return (
    <div className='flex flex-col gap-1'>
      <h3 className='font-medium leading-none text-slate-900 dark:text-white'>{title}</h3>
      <p className='text-sm font-medium leading-none text-slate-500 dark:text-white/70'>{description}</p>
    </div>
  );
};
interface SecuritySettingsProps {
  userId: string;
}

export const SecuritySettings: FunctionComponent<SecuritySettingsProps> = ({ userId }) => {
  const { openDialog } = useDialog();
  const me = useAtomValue(userAtom);
  const { t } = useTranslation('account');
  const twoFactorEnabled = (me as any)?.twoFactorEnabled;
  const twoFactorType = (me as any)?.twoFactorType as 'SMS' | 'AUTHENTICATOR' | null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('security.title')}</CardTitle>
        <CardDescription>{t('security.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Password Section */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <Label title={t('security.password')} description={t('security.passwordDescription')} />
            <Button variant='outline' onClick={() => openDialog(<ChangePasswordDialog userId={userId} />)}>
              <Key className='h-4 w-4 mr-2 stroke-2' />
              {t('security.changePassword')}
            </Button>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <Label title={t('security.twoFactor')} description={t('security.twoFactorDescription')} />
            <div className='flex items-center space-x-2'>
              <Badge variant={twoFactorEnabled ? 'default' : 'secondary'}>
                {twoFactorEnabled ? t('security.enabled') : t('security.notEnabled')}
              </Badge>
              <Button
                variant='outline'
                onClick={() =>
                  openDialog(
                    <Setup2FADialog
                      mobileNumber={(me as any)?.mobileNumber}
                      twoFactorEnabled={twoFactorEnabled}
                      twoFactorType={twoFactorType}
                    />,
                  )
                }>
                <Shield className='h-4 w-4 mr-2 stroke-2' />
                {twoFactorEnabled ? t('security.manage2fa') : t('security.enable2fa')}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
